/**
 * Dataset Redirect Components
 *
 * GW-1728: Redirect components for dataset route changes.
 *
 * - DatasetQueryRedirect: Old query page URL -> dataset detail page
 * - DatasetTestRedirect: Old test path -> new test-query path
 *
 * GW-8103: Param-preserving backward-compat redirects from the retired
 * standalone /developer/datasets/* publisher routes to their /publisher/*
 * equivalents (page rebuilds out of scope — routing only).
 */

import type React from 'react';

import { Navigate, useParams } from 'react-router-dom';

/**
 * Redirects /marketplace/datasets/:datasetId/query/:operationId
 * to /marketplace/datasets/:datasetId
 */
export function DatasetQueryRedirect(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>();
  return <Navigate to={`/marketplace/datasets/${datasetId}`} replace />;
}

/**
 * Redirects /publisher/datasets/:datasetId/test (and the legacy
 * /developer/datasets/:datasetId/test) to /publisher/datasets/:datasetId/test-query
 */
export function DatasetTestRedirect(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>();
  return <Navigate to={`/publisher/datasets/${datasetId}/test-query`} replace />;
}

/**
 * GW-8103: Redirects /developer/datasets/:datasetId/edit
 * to /publisher/datasets/:datasetId/edit
 */
export function DeveloperDatasetEditRedirect(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>();
  return <Navigate to={`/publisher/datasets/${datasetId}/edit`} replace />;
}

/**
 * GW-8103: Redirects /developer/datasets/:datasetId/test-query
 * to /publisher/datasets/:datasetId/test-query
 */
export function DeveloperDatasetTestQueryRedirect(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>();
  return <Navigate to={`/publisher/datasets/${datasetId}/test-query`} replace />;
}

/**
 * GW-8103: Redirects /developer/datasets/:datasetId/queries
 * to /publisher/datasets/:datasetId/queries
 */
export function DeveloperDatasetQueriesRedirect(): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>();
  return <Navigate to={`/publisher/datasets/${datasetId}/queries`} replace />;
}

/**
 * GW-8103: Redirects /developer/datasets/:datasetId/queries/:queryId
 * to /publisher/datasets/:datasetId/queries/:queryId
 */
export function DeveloperDatasetQueryDetailRedirect(): React.JSX.Element {
  const { datasetId, queryId } = useParams<{ datasetId: string; queryId: string }>();
  return <Navigate to={`/publisher/datasets/${datasetId}/queries/${queryId}`} replace />;
}
